import { useElementSize } from '@byecode/ui'
import type { ActorEvent, BlockSize, CardBlockAbstract, CardBlockItemConfig, CardStyle } from '@lighthouse/core'
import { BLOCK_SIZE, CARD_STYLE, EVENT_TYPE } from '@lighthouse/core'
import type { RichTextEditorProps } from '@lighthouse/shared'
import {
    flexMinSizeStyle,
    getCursorStyle,
    getImageFullUrlInApplication,
    getWithScopeIdAndRecordIdAndColumnId,
    IconRender,
    isEmptyRichTextValue,
    RichTextRenderer,
    useActionRunningLoadings,
    useApplicationContext
} from '@lighthouse/shared'
import { Box } from '@mantine/core'
import equal from 'fast-deep-equal'
import { omit } from 'rambda'
import type { FC } from 'react'
import React, { memo, useCallback, useMemo } from 'react'
import styled from 'styled-components'

import { AnimationDecorators } from '../../components'
import { useContainerBlockContext, useCustomViewBlockContext, useEffectiveAnchorId } from '../../context'
import { useScrollToAnchorWhenHashMatch } from '../../hooks/useScrollToAnchorWhenHashMatch'

const CardWrapper = styled(AnimationDecorators)`
    display: flex;
    flex-wrap: wrap;
`

const CardIcon = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
`
const CardContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    word-break: break-word;
`

const ICON_SIZE_MAP = new Map([
    [BLOCK_SIZE.xs, 24],
    [BLOCK_SIZE.sm, 32],
    [BLOCK_SIZE.md, 50],
    [BLOCK_SIZE.lg, 120],
    [BLOCK_SIZE.xl, 180]
])

const FONT_STYLES_MAP = new Map([
    [
        BLOCK_SIZE.xs,
        {
            fontSize: 12
            // lineHeight: '22px'
        }
    ],
    [BLOCK_SIZE.sm, { fontSize: 14 /* lineHeight: '22px' */ }],
    [
        BLOCK_SIZE.md,
        {
            fontSize: 16
            // lineHeight: '24px'
        }
    ],
    [
        BLOCK_SIZE.lg,
        {
            fontSize: 20
            // lineHeight: '28px'
        }
    ],
    [
        BLOCK_SIZE.xl,
        {
            fontSize: 24
            // lineHeight: '32px'
        }
    ]
])

/**
 * 设计要求默认风格透明底
 * @see https://roadmap.byecode.site/P/4e63552d99fd4b01851c36cfbedd91de/D/a130da191ae241bebf623c645c7977e2/a/95ee6ff25df04fb689c7e9f55159ac04/d/ChD21jm645da569cncrLpuCzd8qclma7/v/view-g646f22c9uc1/r/9dc4051519dc469488ad5bf4d8a44690
 */
const BACKGROUND_COLOR = new Map([
    [CARD_STYLE.default, undefined],
    [CARD_STYLE.block, 'var(--color-gray-100)'],
    [CARD_STYLE.card, 'var(--color-white)']
])

type ItemProps = {
    uniqueId: string
    hasCustomCursor?: boolean
    appId: string
    cols: number
    data: CardBlockItemConfig
    config: RichTextEditorProps['config']
    size: BlockSize
    isHorizontal: boolean
    style?: CardStyle
    onClickEvent?: (id: string, actorEvent: ActorEvent) => Promise<void>
}

const Item = memo(
    ({ uniqueId, appId, data, cols, size, style, config, isHorizontal, hasCustomCursor, onClickEvent }: ItemProps) => {
        const hasHeading = useMemo(() => data.heading && !isEmptyRichTextValue(data.heading, config), [config, data.heading])
        const { id, eventList, iconDecoration, iconColor, heading, introduction } = data
        const { loadings, handleActionTriggerWithLoading } = useActionRunningLoadings()
        const hasIntroduction = useMemo(() => introduction && !isEmptyRichTextValue(introduction, config), [config, introduction])
        const clickEvent = eventList?.find(ev => ev.type === EVENT_TYPE.CLICK)
        const hasAction = !!clickEvent

        const itemUniqueId = `${uniqueId}-${id}`

        const handleClick = useCallback(() => {
            if (!onClickEvent || !clickEvent) {
                return
            }
            return handleActionTriggerWithLoading({
                id: itemUniqueId,
                actorEvent: clickEvent,
                trigger: onClickEvent
            })
        }, [clickEvent, handleActionTriggerWithLoading, itemUniqueId, onClickEvent])

        return (
            <Box
                sx={{
                    padding: 'var(--block-padding)',
                    flex: `0 0 ${Math.floor((100 / cols) * 100) / 100}%`,
                    maxWidth: `${Math.floor((100 / cols) * 100) / 100}%`
                }}
            >
                <Box
                    sx={{
                        cursor: hasCustomCursor || !hasAction ? undefined : 'pointer',
                        padding: 10,
                        height: '100%',
                        display: 'flex',
                        flexDirection: isHorizontal ? 'row' : 'column',
                        alignItems: 'center',
                        gap: isHorizontal ? 12 : 8,
                        borderRadius: 12,
                        border: `1px solid ${style === CARD_STYLE.card ? 'var(--color-gray-200)' : 'transparent'}`,
                        backgroundColor: style && BACKGROUND_COLOR.get(style),
                        '&:hover': {
                            border: style === CARD_STYLE.block ? '1px solid var(--color-gray-200)' : undefined,
                            boxShadow: style === CARD_STYLE.card ? '0px 0px 6px 0px rgba(56, 56, 56, 0.12)' : undefined
                        }
                    }}
                    onClick={handleClick}
                >
                    {iconDecoration?.value && (
                        <CardIcon>
                            <IconRender
                                value={iconDecoration}
                                color={iconDecoration?.type === 'icon' ? iconColor : undefined}
                                size={ICON_SIZE_MAP.get(size)}
                                onGetFullUrl={url => getImageFullUrlInApplication(appId, url)}
                                readonly
                                isCompact
                            />
                        </CardIcon>
                    )}
                    <CardContent style={{ alignItems: isHorizontal ? 'flex-start' : 'center' }}>
                        {hasHeading && heading && (
                            <RichTextRenderer value={heading} config={config} style={{ ...FONT_STYLES_MAP.get(size) }} />
                        )}
                        {hasIntroduction && introduction && (
                            <RichTextRenderer
                                value={introduction}
                                config={config}
                                style={{ fontSize: 12, /* lineHeight: '18px', */ textAlign: isHorizontal ? undefined : 'center' }}
                            />
                        )}
                    </CardContent>
                </Box>
            </Box>
        )
    },
    (a, b) => equal(omit('onClick', a), omit('onClick', b))
)

interface CardBlockProps extends React.ComponentPropsWithoutRef<'div'> {
    blockData: CardBlockAbstract
    config?: RichTextEditorProps['config']
    onClickEvent?: (id: string, actorEvent: ActorEvent) => Promise<void>
}

const CardBlock: FC<CardBlockProps> = ({ blockData, config, onClickEvent, ...rest }) => {
    const {
        id,
        title,
        config: { style, size = 'md', list = [], direction = 'vertical', layout = 'auto', cols: fixedCols, breakPoint, cursor, anchor } = {}
    } = blockData
    const { anchorId } = anchor || {}
    const effectiveAnchorId = useEffectiveAnchorId(anchorId)
    useScrollToAnchorWhenHashMatch(effectiveAnchorId, anchor)
    const { appId = '' } = useApplicationContext()
    const { ref, width } = useElementSize<HTMLDivElement>()
    const { scope } = useContainerBlockContext()
    const { record } = useCustomViewBlockContext()
    const uniqueId = useMemo(() => getWithScopeIdAndRecordIdAndColumnId(id, scope, record?.id), [id, scope, record?.id])
    const autoCols = useMemo(() => {
        if (width <= 460) {
            switch (size) {
                case BLOCK_SIZE.xs: {
                    return 3
                }
                case BLOCK_SIZE.sm: {
                    return 2
                }

                default: {
                    return 1
                }
            }
        }
        if (width <= 680) {
            switch (size) {
                case BLOCK_SIZE.xs: {
                    return 4
                }
                case BLOCK_SIZE.sm: {
                    return 3
                }
                case BLOCK_SIZE.md: {
                    return 2
                }

                default: {
                    return 1
                }
            }
        }

        if (width <= 880) {
            switch (size) {
                case BLOCK_SIZE.xs: {
                    return 5
                }
                case BLOCK_SIZE.sm: {
                    return 4
                }
                case BLOCK_SIZE.md: {
                    return 3
                }

                default: {
                    return 2
                }
            }
        }

        if (width <= 1024) {
            switch (size) {
                case BLOCK_SIZE.xs: {
                    return 6
                }
                case BLOCK_SIZE.sm: {
                    return 5
                }
                case BLOCK_SIZE.md: {
                    return 4
                }

                default: {
                    return 3
                }
            }
        }

        if (width <= 1400) {
            switch (size) {
                case BLOCK_SIZE.xs: {
                    return 7
                }
                case BLOCK_SIZE.sm: {
                    return 6
                }

                case BLOCK_SIZE.md: {
                    return 5
                }
                case BLOCK_SIZE.lg: {
                    return 4
                }
                default: {
                    return 3
                }
            }
        }

        if (width > 1400) {
            switch (size) {
                case BLOCK_SIZE.xs: {
                    return 8
                }
                case BLOCK_SIZE.sm: {
                    return 7
                }

                case BLOCK_SIZE.md: {
                    return 6
                }
                case BLOCK_SIZE.lg: {
                    return 5
                }
                default: {
                    return 4
                }
            }
        }

        return 1
    }, [size, width])

    const cols = layout === 'fixed' && fixedCols ? fixedCols : autoCols

    const isHorizontal = direction === 'horizontal'

    const cursorStyle = useMemo(() => getCursorStyle(appId, cursor), [appId, cursor])
    const hasCustomCursor = !!cursorStyle.cursor

    return (
        <CardWrapper
            blockId={id}
            blockName={title}
            animation={breakPoint?.animation}
            id={effectiveAnchorId}
            {...rest}
            style={{
                ...flexMinSizeStyle,
                alignItems: 'stretch',
                justifyContent: 'flex-start',
                height: width === 0 ? 0 : undefined,
                overflow: breakPoint?.size.overflow,
                ...rest.style,
                ...cursorStyle
            }}
            ref={ref}
        >
            {list.map(item => (
                <Item
                    uniqueId={uniqueId}
                    key={item.id}
                    appId={appId}
                    data={item}
                    cols={cols}
                    size={size}
                    isHorizontal={isHorizontal}
                    style={style}
                    config={config}
                    hasCustomCursor={hasCustomCursor}
                    onClickEvent={onClickEvent}
                />
            ))}
        </CardWrapper>
    )
}

export default React.memo(CardBlock)
